import { Subject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  initialize,
  LDClient,
  LDFlagChangeset,
  LDFlagSet,
  LDSingleKindContext,
  LDFlagValue,
} from "launchdarkly-js-client-sdk";

export interface IFlagSet {
  [key: string]: any;
}

export interface IFeatureFlagClient {
  changeUser: (
    newUser: UserSegmentationObject,
    hash?: string,
    onComplete?: (err: Error | null, flags: IFlagSet | null) => void
  ) => Promise<LDFlagSet>;
  onFlagChanged: (flagKey: string) => Observable<IFlagSet>;
  onFlagsChanged: () => Observable<IFlagSet>;
  getFlagValue: (flagKey: string, defaultValue?: LDFlagValue) => LDFlagValue;
  getFlagValueAsync: (flagKey: string, defaultValue?: LDFlagValue) => Promise<LDFlagValue>;
}

// Custom properties on user object.
export interface UserSegmentationObject {
  key: string;
  role?: [string];
  client?: string;
  everestEnabled?: boolean;
  devMachineName?: string;
}

export interface FeatureFlagOptions {
  /**
   * Relay proxy configuration URLs
   */
  baseUrl: string;
  eventsUrl: string;
  streamUrl: string;
}

/**
 * Creates a feature flag client.
 * @param clientId Client id, defaults to clientId found in featureFlagParams on the window, if featureFlagParams is null, defaults to null.
 * @param user User object, defaults to user found in featureFlagParams on the window, if featureFlagParams is null, defaults to null.
 * @param options Feature Flag Options object, defaults to options found in featureFlagParams on the window, if featureFlagParams is null, defaults to null.
 * @param targetFlags Target flags object, allows client to be configured to only focus on specific flags.
 * @returns
 */
export const FeatureFlagClient = (
  clientId: string = window["featureFlagParams"]?.["clientId"],
  user: UserSegmentationObject = window["featureFlagParams"]?.["user"],
  options: FeatureFlagOptions = window["featureFlagParams"]?.["options"],
  targetFlags?: IFlagSet
): IFeatureFlagClient => {
  const _user: LDSingleKindContext = { kind: 'user', ...user };
  let _client: LDClient = null;
  if (clientId) {
    _client = initialize(clientId, _user, options);
  }
  const _targetFlags: IFlagSet = targetFlags;
  const flagChange$: Subject<LDFlagSet> = new Subject<LDFlagSet>();

  /**
   * Event handler for when the client is initialized successfully.
   */
  const handleClientInitialized = () => {
    console.log("Feature Flag Client connected successfully.");
    const output: LDFlagSet = _targetFlags
      ? Object.keys(_targetFlags).reduce((acc, key) => {
          return {
            ...acc,
            [key]: getFlagValue(key),
          };
        }, {})
      : _client.allFlags();
    flagChange$.next(output);
  };

  /**
   * Event handler for when a flag is changed on the server.
   * @param flagChanges
   */
  const handleFlagChange = (flagChanges: LDFlagChangeset) => {
    const output = Object.keys(flagChanges).reduce((acc, key) => {
      return !_targetFlags || typeof _targetFlags[key] != "undefined"
        ? { ...acc, [key]: flagChanges[key].current }
        : { ...acc };
    }, {});

    if (Object.entries(output).length > 0) {
      flagChange$.next(output);
    }
  };

  /**
   * Retrieves the flag value or the default value.
   * @param flagKey
   * @param defaultValue
   * @returns Flag value or the default value.
   */
  const getFlagValue = (
    flagKey: string,
    defaultValue?: LDFlagValue
  ): LDFlagValue => {
    if (!_client) {
      console.log(
        "Get Flag Value requested, but Feature Flag Client not initialized"
      );
      return false;
    }
    return _client.variation(flagKey, defaultValue);
  };

  /**
   * Waits for the client to be initialized successfully.
   * This assumes that the client will connect eventually.
   * @param flagKey
   * @param defaultValue
   * @returns Flag value or the default value.
   */
  const getFlagValueAsync = async (
    flagKey: string,
    defaultValue?: LDFlagValue
  ): Promise<LDFlagValue> => {
    if (!_client) {
      console.log(
        "Get Flag Value Async requested, but Feature Flag Client not initialized"
      );
      return false;
    }
    try {
      await _client.waitForInitialization();
      return getFlagValue(flagKey, defaultValue);
    } catch (error) {
      throw error;
    }
  };

  /**
   * EXPERIMENTAL
   * Observable to subscribe to for listening to flag changes.
   * If targetFlags was set on initialization, it will only output those flags.
   * @returns
   */
  const onFlagsChanged = () => flagChange$.asObservable();

  /**
   * EXPERIMENTAL
   * Observable to subscribe to for listening to one specific flag changes.
   * @param flagKey
   * @returns
   */
  const onFlagChanged = (flagKey: string) =>
    flagChange$.asObservable().pipe(map((newFlags) => newFlags[flagKey]));

  /**
   * Changes the user.
   * Triggers a reload of all feature flags.
   * @param newUser The new user.
   * @param hash If you are using secure mode, the signed user key.
   * @param onComplete A function which will be called once the user has changed and the flags are available.
   * @returns
   */
  const changeUser = (
    newUser: UserSegmentationObject,
    hash?: string,
    onComplete?: (err: Error | null, flags: IFlagSet | null) => void
  ) => {
    return _client.identify(newUser, hash, onComplete);
  };

  if (!_client) {
    console.log("Feature Flag Client not set, unable to initialize");
  } else {
    /**
     * Connect Event handlers.
     */
    _client.on("initialized", handleClientInitialized);
    _client.on("change", handleFlagChange);
  }

  return {
    changeUser,
    onFlagChanged,
    onFlagsChanged,
    getFlagValue,
    getFlagValueAsync,
  };
};
